import {
  all,
  call,
  debounce,
  put,
  select,
  takeEvery,
} from 'redux-saga/effects';
import { actions, types, STORAGE_KEY } from './duck';

const getWizardState = state => state.couponWizard;

// Safe JSON stringify with image size guard
function safeSaveToLocalStorage(state) {
  const snapshot = JSON.parse(JSON.stringify(state));

  const hasDraft =
    snapshot &&
    typeof snapshot === 'object' &&
    snapshot.draft &&
    typeof snapshot.draft === 'object';

  const heroImage =
    hasDraft && snapshot.draft.heroImage ? snapshot.draft.heroImage : null;

  // If image is huge, skip storing the dataUrl to avoid quota errors
  if (heroImage && heroImage.dataUrl && heroImage.dataUrl.length > 1500000) {
    snapshot.draft.heroImage.dataUrl = null;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
}

function* persistWizard() {
  const state = yield select(getWizardState);
  yield call(safeSaveToLocalStorage, state);
}

// Gentle migration to keep old drafts working
function migrateDraft(draft = {}) {
  const migrated = { ...draft };

  // 1) customerSegment -> customerType/customerTypeMulti
  if (migrated.customerSegment && !migrated.customerType) {
    // If a string, keep as customerType; if array, move to customerTypeMulti
    if (Array.isArray(migrated.customerSegment)) {
      migrated.customerTypeMulti = migrated.customerSegment;
    } else {
      migrated.customerType = migrated.customerSegment;
    }
    delete migrated.customerSegment;
  }

  // 2) Ensure perUserLimitType consistency (accept legacy 'one_time')
  if (migrated.perUserLimitType === 'one_time')
    migrated.perUserLimitType = 'oneTime';

  // 3) Normalize expiryDate to a serializable string (ISO) if it looks like a Date
  if (migrated.expiryDate instanceof Date) {
    migrated.expiryDate = migrated.expiryDate.toISOString();
  }

  return migrated;
}

function* rehydrate() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // migrate in-memory before storing back
      const migrated = {
        ...parsed,
        draft: migrateDraft(parsed.draft),
      };
      yield put(actions.rehydrateDone(migrated));
      // also persist migrated shape back to storage
      yield call(safeSaveToLocalStorage, migrated);
    } else {
      yield put(actions.rehydrateDone(null));
    }
  } catch (e) {
    yield put(actions.rehydrateDone(null));
  }
}

function* clearStorage() {
  localStorage.removeItem(STORAGE_KEY);
}

// Persist on these actions (debounced for 300ms)
export default function* saga() {
  yield all([
    takeEvery(types.REHYDRATE_FROM_STORAGE, rehydrate),
    debounce(
      300,
      [
        types.UPDATE_DRAFT,
        types.ATTACH_HERO_IMAGE,
        types.CLEAR_HERO_IMAGE,
        types.SET_STEP,
      ],
      persistWizard,
    ),
    takeEvery(types.RESET_WIZARD, clearStorage),
  ]);
}
